import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, Heart } from "lucide-react";
import { LanguageSelector } from "./LanguageSelector";
import { useBookmarks } from "../lib/bookmarks";

export const Header = ({ categories = [] }) => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const termsBag = useBookmarks("terms");
  const productsBag = useBookmarks("products");
  const totalBookmarks = termsBag.count + productsBag.count;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length === 0) return;
    setSearchOpen(false);
    setOpen(false);
    navigate(`/recherche?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-bl-border" : "bg-white/85 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
            <img
              src="/beckyliss-logo-header.png"
              alt="Beckyliss Glossary — by Beckyliss Cosmetics"
              className="h-6 md:h-8 w-auto object-contain"
              data-testid="header-logo-img"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav">
            {categories.slice(0, 6).map((c) => (
              <Link
                key={c.slug}
                to={`/categorie/${c.slug}`}
                className="text-[13px] tracking-wide text-bl-mauveDeep hover:text-bl-fuchsia underline-grow transition-colors"
                data-testid={`nav-cat-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/top-formules"
              className="text-[13px] tracking-wide text-bl-dore hover:text-bl-fuchsia underline-grow transition-colors flex items-center gap-1"
              data-testid="nav-top-formules"
            >
              <span aria-hidden style={{ fontSize: 12 }}>★</span> Top formules
            </Link>
            <Link
              to="/quiz"
              className="quiz-link relative font-serif italic text-xl md:text-2xl text-pink-500 hover:text-pink-600 transition-colors px-2"
              data-testid="nav-quiz"
            >
              Quiz
              <Heart size={12} strokeWidth={2} className="quiz-heart" />
              <Heart size={10} strokeWidth={2} className="quiz-heart" />
              <Heart size={14} strokeWidth={2} className="quiz-heart" />
              <Heart size={9}  strokeWidth={2} className="quiz-heart" />
              <Heart size={11} strokeWidth={2} className="quiz-heart" />
            </Link>
          </nav>

          <div className="flex items-center gap-1 md:gap-2">
            <Link
              to="/mon-glossaire"
              aria-label={`Mon glossaire — ${totalBookmarks} favori${totalBookmarks > 1 ? 's' : ''}`}
              className="relative w-10 h-10 grid place-items-center rounded-full hover:bg-bl-rose/60 transition-colors"
              data-testid="header-bookmarks-link"
            >
              <Heart
                size={18}
                strokeWidth={1.5}
                fill={totalBookmarks > 0 ? "currentColor" : "none"}
                className={totalBookmarks > 0 ? "text-bl-fuchsia" : ""}
              />
              {totalBookmarks > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-bl-fuchsia text-white text-[10px] font-medium"
                  data-testid="header-bookmarks-count"
                >
                  {totalBookmarks}
                </span>
              )}
            </Link>
            <LanguageSelector />
            <button
              aria-label="Rechercher"
              onClick={() => setSearchOpen((s) => !s)}
              className="w-10 h-10 grid place-items-center rounded-full hover:bg-bl-rose/60 transition-colors"
              data-testid="search-toggle-btn"
            >
              <Search size={18} strokeWidth={1.5} />
            </button>
            <button
              aria-label="Menu"
              onClick={() => setOpen((s) => !s)}
              className="lg:hidden w-10 h-10 grid place-items-center rounded-full hover:bg-bl-rose/60 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submit} className="pb-6" data-testid="search-form">
            <div className="flex items-center border-b border-bl-dore-light/80 pb-2">
              <Search size={16} strokeWidth={1.5} className="text-bl-textSoft mr-3" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un terme, un produit, une référence (SKU)…"
                className="flex-1 bg-transparent border-none outline-none text-base text-bl-text placeholder:text-bl-textSoft/70 font-light"
                data-testid="search-input"
              />
              <button
                type="submit"
                className="overline ml-3 hover:text-bl-text transition-colors"
                data-testid="search-submit-btn"
              >
                Chercher
              </button>
            </div>
          </form>
        )}

        {open && (
          <div className="lg:hidden pb-6 border-t border-bl-border" data-testid="mobile-menu">
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 pt-4">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/categorie/${c.slug}`}
                    onClick={() => setOpen(false)}
                    className="text-sm tracking-wide text-bl-mauveDeep hover:text-bl-fuchsia block py-1 underline-grow transition-colors"
                    data-testid={`mobile-nav-cat-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};
